/** @format */

import {
  BRAND_FILTER_ID,
  CATEGORY_FILTER_ID,
  GTGT_FILTER_NAME,
  GTGT_TITLE,
  KIDS_SHOES_SIZE,
  MENS_SHOES_SIZE,
  PRICE_FILTER_ID_DSG,
  PRICE_FILTER_ID_GG,
  PRICE_FILTER_ID_PL,
  RATINGS_FILTER_ID,
  SIZE_FILTER_ID,
  WOMENS_SHOES_SIZE,
} from '@plp/data-access/constants';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  model,
  output,
} from '@angular/core';
import {
  FacetAccordionElement,
  FacetVO,
  FilterUpdatePayload,
  RefinementUpdateObj,
} from '@plp/data-access/interfaces';
import { HomefieldCheckboxModule, HomefieldTextLinkModule } from '@ecomm-patterns-components';
import {
  HomefieldIconLibraryModule,
  HomefieldIconsLibraryService,
  homefieldIconSearchOutline,
} from '@dsg-tech/homefield-angular-icons';
import { NgTemplateOutlet } from '@angular/common';
import { ProductListLayoutService, ProductListUIService } from '@plp/data-access/services';
import {
  selectFeatureFlags,
  selectProductListCategoryDetails,
  selectProductListSEO,
  selectSelectedFilters,
  selectedFiltersUpdated,
} from '@plp/data-access/state';

import { BrandFilterComponent } from './brand-filter/brand-filter.component';
import { CategoryFilterComponent } from './category-filter/category-filter.component';
import { FilterIdPipe } from '../pipes/filter-id.pipe';
import { FilterType } from './filter.enums';
import { FormsModule } from '@angular/forms';
import { HmfAccordionComponent, HmfActionTextComponent } from '@dsg-tech/homefield-angular';
import { PriceFilterComponent } from './price-filter/price-filter.component';
import { RatingFilterComponent } from './rating-filter/rating-filter.component';
import { SizeFilterComponent } from './size-filter/size-filter.component';
import { StandardFilterComponent } from './standard-filter/standard-filter.component';
import { Store } from '@ngrx/store';
import { UtilityService } from '@shared/data-access/services';
import { injectFocusRestore } from '@plp/utils';

@Component({
  selector: 'plp-components-filter',
  imports: [
    StandardFilterComponent,
    BrandFilterComponent,
    PriceFilterComponent,
    SizeFilterComponent,
    FormsModule,
    HmfActionTextComponent,
    HomefieldCheckboxModule,
    HomefieldIconLibraryModule,
    HomefieldTextLinkModule,
    RatingFilterComponent,
    CategoryFilterComponent,
    NgTemplateOutlet,
    FilterIdPipe,
    HmfAccordionComponent,
  ],
  templateUrl: './filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterComponent {
  private readonly store = inject(Store);
  private readonly productListUIService = inject(ProductListUIService);
  private readonly productListLayoutService = inject(ProductListLayoutService);
  private readonly utilityService = inject(UtilityService);
  private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
  // Restore focus to the control after a filter reload; if it no longer exists (e.g. the whole
  // filter section is gone), ask the container to move focus to the first accordion.
  private readonly focusRestore = injectFocusRestore(
    computed(() => !this.disabled()),
    {
      onMissing: () => this.focusLostAfterReload.emit(),
    },
  );

  readonly showMore = model<boolean>(false);

  readonly filter = input.required<FacetVO>();
  readonly disabled = input(false);
  readonly defaultOpen = input(false);
  readonly isMobile = input(false);
  readonly isPlp3Template = input(false);
  readonly isExposedFilter = input<boolean>(false);
  readonly refinementSource = input<string | undefined>();

  readonly facetToggled = output<FacetAccordionElement>();
  readonly accordionFocused = output<void>();
  readonly focusLostAfterReload = output<void>();
  readonly selectedOptionsChange = output<string[]>();
  readonly filterSelectionCommitted = output<void>();

  readonly productListSEOData = this.store.selectSignal(selectProductListSEO);
  readonly selectedFilters = this.store.selectSignal(selectSelectedFilters);
  readonly productListCategoryDetails = this.store.selectSignal(selectProductListCategoryDetails);
  readonly featureFlags = this.store.selectSignal(selectFeatureFlags);

  readonly filterType = computed(() => {
    switch (this.filter().attrIdentifier) {
      case BRAND_FILTER_ID:
        return FilterType.Brand;
      case PRICE_FILTER_ID_DSG:
      case PRICE_FILTER_ID_GG:
      case PRICE_FILTER_ID_PL:
        return FilterType.Price;
      case CATEGORY_FILTER_ID:
        return FilterType.Category;
      case SIZE_FILTER_ID:
      case KIDS_SHOES_SIZE:
      case WOMENS_SHOES_SIZE:
      case MENS_SHOES_SIZE:
        return FilterType.Size;
      case RATINGS_FILTER_ID:
        return FilterType.Ratings;
      default:
        return FilterType.Standard;
    }
  });

  readonly selectedOptions = linkedSignal<string[], string[]>({
    source: () => this.selectedFilters()[this.filter().attrIdentifier] || [],
    computation: (incoming, previous) => {
      const currentOptionsSet = new Set(previous?.value);
      // If incoming options are empty when we previously had values, or the selected options differ, update the options
      if (
        incoming.length !== currentOptionsSet.size ||
        incoming.some((option) => !currentOptionsSet.has(option))
      ) {
        return [...incoming];
      }
      return previous?.value || [];
    },
  });

  readonly selectedSubheaders = computed(() => {
    const selectedOptions = this.selectedOptions();
    if (!selectedOptions.length) {
      return '';
    }

    let subheaders: string[];
    const filterType = this.filterType();
    const categoryDetails = this.productListCategoryDetails();
    if (filterType === FilterType.Category && categoryDetails) {
      subheaders = this.selectedOptions().map((element) => categoryDetails[element]?.name);
    } else if (filterType === FilterType.Price) {
      // format the subheaders for the price filter, i.e. *-25 -> Under $25, 500-* -> $500 & Above
      subheaders = this.selectedOptions().map((option) =>
        this.productListUIService.addSymbolForPriceDisplay(option),
      );
    } else if (filterType === FilterType.Ratings) {
      subheaders = this.selectedOptions().map((element) => {
        const rating = element.replace('+', '');
        return rating === '5' ? `${rating} stars` : `${rating} stars & up`;
      });
    } else {
      subheaders = [...this.selectedOptions()];
    }
    return subheaders.join(', ');
  });

  readonly filterShouldBeDisplayed = computed(() => {
    if (this.filterType() === FilterType.Category) {
      return this.productListSEOData() === null || this.isPlp3Template();
    }
    if (this.filterType() === FilterType.Ratings) {
      return this.featureFlags().filterByCustomerRatingEnabled;
    }
    return true;
  });

  GTGT_TITLE = GTGT_TITLE;
  GTGT_FILTER_NAME = GTGT_FILTER_NAME;
  FilterType = FilterType;

  constructor() {
    this.homefieldIconsLibraryService.registerIcons([homefieldIconSearchOutline]);

    effect(() => {
      this.selectedOptionsChange.emit(this.selectedOptions());
    });
  }

  facetToggleChanged(attributeName: string, open: boolean, showMore: boolean): void {
    this.facetToggled.emit({
      attrName: attributeName,
      open,
      showMore,
    });
  }

  onAccordionActiveChanged(open: boolean): void {
    if (open === this.defaultOpen()) {
      return;
    }

    this.facetToggleChanged(this.filter().attrName, open, false);
  }

  async setSelectedFilter(filterName: string, radio = false): Promise<void> {
    if (this.disabled()) {
      return;
    }

    let refinementUpdateObj: RefinementUpdateObj | undefined = undefined;

    const selectedOptionsSet = new Set(this.selectedOptions());
    if (radio) {
      if (!selectedOptionsSet.has(filterName)) {
        this.selectedOptions.set([filterName]);
        refinementUpdateObj = this.buildRefinementUpdateObj(filterName);
      }
    } else {
      // If the clicked on option is already selected, remove it from the array
      if (selectedOptionsSet.has(filterName)) {
        selectedOptionsSet.delete(filterName);
      } else {
        if (this.filterType() === FilterType.Category) {
          selectedOptionsSet.clear();
        }
        selectedOptionsSet.add(filterName);
        refinementUpdateObj = this.buildRefinementUpdateObj(filterName);
      }
      this.selectedOptions.set([...selectedOptionsSet]);
    }

    await this.afterFilterChange(refinementUpdateObj);
    this.filterSelectionCommitted.emit();
  }

  async handleClearClick(event: MouseEvent) {
    event.preventDefault();

    await this.clearSelectedFilters();
  }

  private async clearSelectedFilters() {
    this.selectedOptions.set([]);

    await this.afterFilterChange();
    this.filterSelectionCommitted.emit();
  }

  private async afterFilterChange(refinementUpdateObj?: RefinementUpdateObj | undefined) {
    this.focusRestore.capture();
    this.productListLayoutService.scrollToTop();

    // Validate if the browser supports Scheduler.yield fn
    await this.utilityService.yieldTask(() => this.dispatchActions(refinementUpdateObj));
  }

  private dispatchActions(refinementUpdateObj: RefinementUpdateObj | undefined): void {
    const filterType: string = this.filter().attrIdentifier;
    const selectedFiltersPayload: FilterUpdatePayload = {
      selectedFilters: {
        [filterType]: this.selectedOptions(),
      },
      refinementUpdateObj,
    };

    this.store.dispatch(selectedFiltersUpdated(selectedFiltersPayload));
  }

  private buildRefinementUpdateObj(filterName: string): RefinementUpdateObj {
    const refinementUpdateObj: RefinementUpdateObj = {
      UpdateType: 'Refinement',
      UpdateValue: {
        RefinementValue: filterName,
        RefinementAttribute: this.filter().attrName,
        ProductCountTotal: undefined,
      },
    };
    if (this.refinementSource()) {
      refinementUpdateObj.UpdateValue.RefinementSource = this.refinementSource();
    }
    return refinementUpdateObj;
  }

  onAccordionFocus(): void {
    this.accordionFocused.emit();
  }
}
